import { useState, useEffect } from "react";
import {
  Card,
  CardContent,
  Typography,
  Box,
  Button,
  TextField,
  Autocomplete,
  Alert,
  Grid,
  Divider,
} from "@mui/material";
import api, { apiService } from "../../services/api";

// Patient self-service: join an existing verified practice (instant), or
// propose a brand-new one (goes to admin review). A patient with no
// practice at all is a normal, expected state — not an error — so this
// never blocks anything else on the profile page while unset.
export default function MyPracticeCard() {
  const [currentPractice, setCurrentPractice] = useState(null); // from patient profile, if any
  const [mode, setMode] = useState(null); // "join" | "propose" | null
  const [practiceSearchInput, setPracticeSearchInput] = useState("");
  const [practiceOptions, setPracticeOptions] = useState([]);
  const [searching, setSearching] = useState(false);
  const [selectedPractice, setSelectedPractice] = useState(null);
  const [newPractice, setNewPractice] = useState({ practiceName: "", addressLine1: "", city: "", state: "", zip: "", phone: "" });
  const [submitting, setSubmitting] = useState(false);
  const [message, setMessage] = useState(null); // { text, severity }

  useEffect(() => {
    api.get("/v1/patients/me/practice")
      .then((res) => {
        if (res.data?.organizationName) {
          setCurrentPractice(res.data.organizationName);
        }
      })
      .catch(() => {
        // No practice yet is a normal, expected state — this card just
        // shows it, not an error.
      });
  }, []);

  useEffect(() => {
    if (!practiceSearchInput || practiceSearchInput.length < 2) {
      setPracticeOptions([]);
      return;
    }
    setSearching(true);
    const timeout = setTimeout(async () => {
      try {
        const res = await api.get("/v1/organizations/search", { params: { q: practiceSearchInput } });
        setPracticeOptions(res.data || []);
      } catch (err) {
        setPracticeOptions([]);
      } finally {
        setSearching(false);
      }
    }, 350);
    return () => clearTimeout(timeout);
  }, [practiceSearchInput]);

  const handleJoin = async () => {
    if (!selectedPractice) return;
    setSubmitting(true);
    try {
      const result = await apiService.setMyPracticeAsPatient({ organizationId: selectedPractice.id });
      setCurrentPractice(result.organizationName);
      setMode(null);
      setMessage({ text: `You've joined ${result.organizationName}.`, severity: "success" });
    } catch (err) {
      setMessage({ text: err.response?.data?.error || "Failed to join practice.", severity: "error" });
    } finally {
      setSubmitting(false);
    }
  };

  const handlePropose = async () => {
    if (!newPractice.practiceName.trim()) return;
    setSubmitting(true);
    try {
      const result = await apiService.setMyPracticeAsPatient(newPractice);
      setMode(null);
      setNewPractice({ practiceName: "", addressLine1: "", city: "", state: "", zip: "", phone: "" });
      setMessage({ text: result.message || "Submitted for admin review.", severity: "success" });
    } catch (err) {
      setMessage({ text: "Failed to submit practice request.", severity: "error" });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Card variant="outlined">
      <CardContent>
        <Typography variant="subtitle1" fontWeight="bold" gutterBottom>My Practice</Typography>

        {message && <Alert severity={message.severity} sx={{ mb: 2 }}>{message.text}</Alert>}

        {currentPractice ? (
          <Typography variant="body2" color="text.secondary" paragraph>
            You're currently part of <strong>{currentPractice}</strong>.
          </Typography>
        ) : (
          <Typography variant="body2" color="text.secondary" paragraph>
            You're not currently part of a practice. You can join one, or propose a new one if
            yours isn't listed yet.
          </Typography>
        )}

        {!mode && (
          <Box sx={{ display: "flex", gap: 1 }}>
            <Button size="small" variant="outlined" onClick={() => setMode("join")}>
              {currentPractice ? "Switch Practice" : "Join a Practice"}
            </Button>
            <Button size="small" variant="text" onClick={() => setMode("propose")}>
              Propose a New Practice
            </Button>
          </Box>
        )}

        {mode === "join" && (
          <Box sx={{ mt: 2 }}>
            <Autocomplete
              options={practiceOptions}
              loading={searching}
              getOptionLabel={(option) =>
                typeof option === "string" ? option : `${option.name}${option.city ? ` — ${option.city}, ${option.state}` : ""}`
              }
              inputValue={practiceSearchInput}
              onInputChange={(e, v) => setPracticeSearchInput(v)}
              value={selectedPractice}
              onChange={(e, v) => setSelectedPractice(v)}
              renderInput={(params) => <TextField {...params} label="Search practices" placeholder="Start typing a practice name" fullWidth />}
            />
            <Box sx={{ display: "flex", gap: 1, mt: 2 }}>
              <Button size="small" onClick={() => { setMode(null); setSelectedPractice(null); }}>Cancel</Button>
              <Button size="small" variant="contained" disabled={!selectedPractice || submitting} onClick={handleJoin}>
                {submitting ? "Joining…" : "Join"}
              </Button>
            </Box>
          </Box>
        )}

        {mode === "propose" && (
          <Box sx={{ mt: 2 }}>
            <Alert severity="info" sx={{ mb: 2 }}>
              A new practice needs a quick admin review before it's assigned to you.
            </Alert>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <TextField fullWidth size="small" label="Practice Name" value={newPractice.practiceName}
                  onChange={(e) => setNewPractice({ ...newPractice, practiceName: e.target.value })} />
              </Grid>
              <Grid item xs={12}>
                <TextField fullWidth size="small" label="Address" value={newPractice.addressLine1}
                  onChange={(e) => setNewPractice({ ...newPractice, addressLine1: e.target.value })} />
              </Grid>
              <Grid item xs={6}>
                <TextField fullWidth size="small" label="City" value={newPractice.city}
                  onChange={(e) => setNewPractice({ ...newPractice, city: e.target.value })} />
              </Grid>
              <Grid item xs={3}>
                <TextField fullWidth size="small" label="State" value={newPractice.state}
                  onChange={(e) => setNewPractice({ ...newPractice, state: e.target.value })} />
              </Grid>
              <Grid item xs={3}>
                <TextField fullWidth size="small" label="Zip" value={newPractice.zip}
                  onChange={(e) => setNewPractice({ ...newPractice, zip: e.target.value })} />
              </Grid>
            </Grid>
            <Box sx={{ display: "flex", gap: 1, mt: 2 }}>
              <Button size="small" onClick={() => setMode(null)}>Cancel</Button>
              <Button size="small" variant="contained" disabled={!newPractice.practiceName.trim() || submitting} onClick={handlePropose}>
                {submitting ? "Submitting…" : "Submit for Review"}
              </Button>
            </Box>
          </Box>
        )}
      </CardContent>
    </Card>
  );
}